import { Component } from '@theme/component';

/**
 * @typedef {Object} BreadcrumbsRefs
 * @property {HTMLButtonElement} backButton
 * @property {HTMLElement} container
 * @property {HTMLTemplateElement} separatorTemplate
 */

/**
 * @typedef {Object} BreadcrumbItem
 * @property {string} title
 * @property {string} url
 */

/**
 * @typedef {Object} MenuItem
 * @property {string} title
 * @property {string} url
 * @property {MenuItem[]=} subMenu
 * @property {string | number=} collectionId
 */

/**
 * @typedef {Object} MenuData
 * @property {MenuItem[]=} menuItems
 */

/**
 * @typedef {Object} ProductCollection
 * @property {string=} handle
 * @property {string | number=} id
 * @property {string=} url
 */

// Keep in sync with algolia-search-shared.js
const PLP_FILTERS_STORAGE_KEY = 'gh:last-plp-filters';

/**
 * @extends {Component<BreadcrumbsRefs>}
 */
class Breadcrumbs extends Component {
  requiredRefs = ['backButton', 'container', 'separatorTemplate'];

  /** @type {SVGElement | null} */
  #separatorPrototype = null;

  #connected = false;

  connectedCallback() {
    super.connectedCallback();
    if (this.#connected) return;
    this.#connected = true;

    this.refs.backButton.addEventListener('click', this.#onBackClick);
    this.#separatorPrototype = this.#createSeparatorPrototype();
    this.#render();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#connected = false;
    this.refs.backButton.removeEventListener('click', this.#onBackClick);
  }

  /**
   * @param {MouseEvent} event
   */
  #onBackClick = (event) => {
    event.preventDefault();
    window.history.back();
  };

  /**
   * @returns {MenuData | undefined}
   */
  static getMenuData() {
    return /** @type {MenuData | undefined} */ (/** @type {any} */ (window).menu_json);
  }

  /**
   * @returns {ProductCollection[]}
   */
  static getProductCollections() {
    const collections = /** @type {unknown} */ (/** @type {any} */ (window).product_collections);
    if (Array.isArray(collections)) {
      return /** @type {ProductCollection[]} */ (collections);
    }

    if (collections && typeof collections === 'object' && 'handle' in collections) {
      return /** @type {ProductCollection[]} */ ([collections]);
    }

    return [];
  }

  get prefix() {
    const theme =
      /** @type {any} */ (window).Theme ||
      (typeof Theme !== 'undefined' ? /** @type {any} */ (Theme) : undefined);
    return theme?.routes?.root_url_prefix || '/';
  }

  getUrl(url) {
    const theme =
      /** @type {any} */ (window).Theme ||
      (typeof Theme !== 'undefined' ? /** @type {any} */ (Theme) : undefined);
    if (typeof theme?.getLocalizedUrl === 'function') {
      return theme.getLocalizedUrl(url);
    }

    const origin = theme?.routes?.request_origin;
    return (url || '').replace(`${origin}/`, this.prefix);
  }

  /**
   * @param {string} path
   * @returns {string}
   */
  static stripLocalePrefix(path) {
    const reserved = new Set([
      'collections',
      'products',
      'pages',
      'blogs',
      'cart',
      'account',
      'search',
      'apps',
      'policies',
    ]);
    // "/de/collections/..." -> "/collections/..." and "/fr-CH/products/..." -> "/products/..."
    return (
      path.replace(/^\/([a-z]{2}(?:-[A-Za-z]{2})?)(?=\/|$)/i, (match, locale) =>
        reserved.has(String(locale).toLowerCase()) ? match : ''
      ) || '/'
    );
  }

  /**
   * @param {unknown} id
   * @returns {string}
   */
  static normalizeResourceId(id) {
    if (id == null || id === '') return '';
    const match = String(id).match(/(\d+)\s*$/);
    return match ? match[1] : String(id);
  }

  /**
   * @param {unknown} url
   * @returns {string}
   */
  static getCollectionHandle(url) {
    const path = Breadcrumbs.stripLocalePrefix(Breadcrumbs.normalizePathname(url));
    const marker = '/collections/';
    const index = path.indexOf(marker);
    if (index === -1) return '';
    return path.slice(index + marker.length).split('/')[0] || '';
  }

  /**
   * @param {unknown} url
   * @returns {string}
   */
  static normalizePathname(url) {
    const urlString = typeof url === 'string' ? url : '';
    if (!urlString) return '/';
    try {
      const urlObj = new URL(urlString, window.location.origin);
      return urlObj.pathname.replace(/\/$/, '') || '/';
    } catch {
      const queryIndex = urlString.indexOf('?');
      const withoutQuery = queryIndex === -1 ? urlString : urlString.slice(0, queryIndex);
      return withoutQuery.replace(/\/$/, '') || '/';
    }
  }

  #render() {
    const menuData = Breadcrumbs.getMenuData();
    const menuItems = menuData?.menuItems;
    if (!Array.isArray(menuItems) || menuItems.length === 0) return;

    const currentPath = window.location.pathname;
    const strippedPath = Breadcrumbs.stripLocalePrefix(currentPath);
    const currentUrl = window.location.origin + strippedPath;
    const isProductPage = strippedPath.includes('/products/');

    /** @type {BreadcrumbItem[]} */
    let breadcrumbs = [];

    if (isProductPage) {
      breadcrumbs = this.#findProductBreadcrumbs(menuItems);
    }

    if (breadcrumbs.length === 0) {
      breadcrumbs = this.#findBreadcrumbPath(menuItems, currentPath);
      if (breadcrumbs.length === 0) {
        breadcrumbs = this.#findBreadcrumbPath(menuItems, strippedPath);
      }
      if (breadcrumbs.length === 0) {
        breadcrumbs = this.#findBreadcrumbPath(menuItems, currentUrl);
      }
    }

    if (breadcrumbs.length === 0) return;

    this.#renderBreadcrumbs(breadcrumbs, isProductPage);
  }

  /**
   * @param {MenuItem[]} menuItems
   * @returns {BreadcrumbItem[]}
   */
  #findProductBreadcrumbs(menuItems) {
    const productCollections = Breadcrumbs.getProductCollections();
    if (productCollections.length === 0) return [];

    /** @type {BreadcrumbItem[]} */
    let bestMatch = [];
    let bestDepth = 0;

    for (const collection of productCollections) {
      /** @type {string[]} */
      const candidates = [];
      if (collection?.handle) candidates.push(`/collections/${collection.handle}`);
      if (collection?.url) candidates.push(collection.url);

      let breadcrumbs = [];
      for (const candidate of candidates) {
        breadcrumbs = this.#findBreadcrumbPath(menuItems, candidate);
        if (breadcrumbs.length > 0) break;
      }

      if (breadcrumbs.length === 0 && collection?.id) {
        breadcrumbs = this.#findBreadcrumbPathById(menuItems, collection.id);
      }

      if (breadcrumbs.length > bestDepth) {
        bestMatch = breadcrumbs;
        bestDepth = breadcrumbs.length;
      }
    }

    return bestMatch;
  }

  /**
   * @param {MenuItem[]} menuItems
   * @param {string} targetUrl
   * @param {BreadcrumbItem[]} parentPath
   * @returns {BreadcrumbItem[]}
   */
  #findBreadcrumbPath(menuItems, targetUrl, parentPath = []) {
    const normalizedTargetUrl = Breadcrumbs.normalizePathname(targetUrl);
    const strippedTargetUrl = Breadcrumbs.stripLocalePrefix(normalizedTargetUrl);
    const targetHandle = Breadcrumbs.getCollectionHandle(targetUrl);
    const localizedTargetUrl = Breadcrumbs.normalizePathname(this.getUrl(targetUrl));

    for (const item of menuItems) {
      const currentPath = [...parentPath, { title: item.title, url: item.url }];

      const normalizedItemUrl = Breadcrumbs.normalizePathname(item.url);
      const strippedItemUrl = Breadcrumbs.stripLocalePrefix(normalizedItemUrl);
      const localizedItemUrl = Breadcrumbs.normalizePathname(this.getUrl(item.url));
      const itemHandle = Breadcrumbs.getCollectionHandle(item.url);

      if (
        normalizedItemUrl === normalizedTargetUrl ||
        item.url === targetUrl ||
        strippedItemUrl === strippedTargetUrl ||
        localizedItemUrl === normalizedTargetUrl ||
        localizedItemUrl === strippedTargetUrl ||
        localizedItemUrl === localizedTargetUrl ||
        (itemHandle && targetHandle && itemHandle === targetHandle)
      ) {
        return currentPath;
      }

      if (Array.isArray(item.subMenu) && item.subMenu.length > 0) {
        const subPath = this.#findBreadcrumbPath(item.subMenu, targetUrl, currentPath);
        if (subPath.length > 0) return subPath;
      }
    }

    return [];
  }

  /**
   * @param {MenuItem[]} menuItems
   * @param {string | number} targetId
   * @param {BreadcrumbItem[]} parentPath
   * @returns {BreadcrumbItem[]}
   */
  #findBreadcrumbPathById(menuItems, targetId, parentPath = []) {
    const normalizedTargetId = Breadcrumbs.normalizeResourceId(targetId);
    if (!normalizedTargetId) return [];

    for (const item of menuItems) {
      const currentPath = [...parentPath, { title: item.title, url: item.url }];

      if (
        item.collectionId &&
        Breadcrumbs.normalizeResourceId(item.collectionId) === normalizedTargetId
      ) {
        return currentPath;
      }

      if (Array.isArray(item.subMenu) && item.subMenu.length > 0) {
        const subPath = this.#findBreadcrumbPathById(item.subMenu, targetId, currentPath);
        if (subPath.length > 0) return subPath;
      }
    }

    return [];
  }

  /**
   * Handle of the collection page the shopper came from, or '' when the previous page was
   * anything else (another product, home page, other origin, no referrer). Collection-scoped
   * product URLs (/collections/x/products/y) are not collection pages.
   * @returns {string}
   */
  static getReferrerCollectionHandle() {
    try {
      if (!document.referrer) return '';
      const referrer = new URL(document.referrer);
      if (referrer.origin !== window.location.origin) return '';

      const path = Breadcrumbs.stripLocalePrefix(Breadcrumbs.normalizePathname(referrer.pathname));
      return path.match(/^\/collections\/([^/]+)$/)?.[1] || '';
    } catch {
      return '';
    }
  }

  /**
   * Appends the filters the shopper last used on a collection to the link that points to
   * that collection. Written by algolia-search-shared.js.
   * @param {string} href
   * @returns {string}
   */
  #withStoredFilters(href) {
    try {
      const stored = JSON.parse(sessionStorage.getItem(PLP_FILTERS_STORAGE_KEY) || 'null');
      if (!stored?.search || Breadcrumbs.getCollectionHandle(href) !== stored.handle) return href;

      // The stored filters only count while the page before this one is that very collection.
      // The referrer survives reloads and history navigation, so the result never depends on
      // whether the entry was already read once, and a detour (home page, another product)
      // never brings old filters back.
      if (Breadcrumbs.getReferrerCollectionHandle() !== stored.handle) return href;

      const url = new URL(href, window.location.origin);
      if (url.origin !== window.location.origin || url.search) return href;

      return `${url.pathname}${stored.search}${url.hash}`;
    } catch {
      return href;
    }
  }

  #createSeparatorPrototype() {
    const node = this.refs.separatorTemplate.content.firstElementChild;
    if (!(node instanceof SVGElement)) return null;

    const svg = /** @type {SVGElement} */ (node.cloneNode(true));
    svg.classList.add('breadcrumbs__breadcrumb-icon');
    svg.setAttribute('aria-hidden', 'true');
    svg.setAttribute('focusable', 'false');
    return svg;
  }

  #getSeparatorNode() {
    if (!this.#separatorPrototype) return null;
    return /** @type {SVGElement} */ (this.#separatorPrototype.cloneNode(true));
  }

  /**
   * @param {BreadcrumbItem[]} breadcrumbs
   * @param {boolean} isProductPage
   */
  #renderBreadcrumbs(breadcrumbs, isProductPage) {
    const currentPath = Breadcrumbs.stripLocalePrefix(window.location.pathname);
    const currentUrl = window.location.origin + currentPath;
    const isCurrentProductPage =
      isProductPage || currentPath.includes('/products/') || currentUrl.includes('/products/');

    this.refs.container.innerHTML = '';

    breadcrumbs.forEach((crumb, index) => {
      const isLast = index === breadcrumbs.length - 1;

      if (isLast) {
        if (isCurrentProductPage) {
          const link = document.createElement('a');
          link.href = this.#withStoredFilters(this.getUrl(crumb.url));
          link.textContent = crumb.title;
          link.className = 'breadcrumbs__breadcrumb-title';
          this.refs.container.appendChild(link);
        } else {
          const span = document.createElement('span');
          span.textContent = crumb.title;
          span.className = 'breadcrumbs__last-breadcrumb';
          this.refs.container.appendChild(span);
        }

        return;
      }

      const link = document.createElement('a');
      link.href = this.#withStoredFilters(this.getUrl(crumb.url));
      link.textContent = crumb.title;
      link.className = 'breadcrumbs__breadcrumb-title';
      this.refs.container.appendChild(link);

      const separatorNode = this.#getSeparatorNode();
      if (separatorNode) this.refs.container.appendChild(separatorNode);
    });
  }
}

if (!customElements.get('bread-crumbs')) {
  customElements.define('bread-crumbs', Breadcrumbs);
}
